import { useCallback, useEffect, useState } from 'react';
import { expectArray } from '../../../apiShape.js';
import { toUserFacingMessage } from '../../../userFacingError.js';
/**
 * useMondayExportState — Board → Group, the Basecamp Project → To-do list
 * shape. A monday item lives in a group on a board, and the export
 * destination is that pair (server lib/monday.ts MondayDestination).
 */
export function useMondayExportState(runtime) {
    const [boards, setBoards] = useState([]);
    const [boardId, setBoardId] = useState('');
    const [groups, setGroups] = useState([]);
    const [groupId, setGroupId] = useState('');
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupsError, setGroupsError] = useState(null);
    const [groupsRetry, setGroupsRetry] = useState(0);
    // Clear the child in the gesture itself, before the next effect can run, so
    // Export never sees a group from the previous board during loading.
    const chooseBoard = useCallback((value) => {
        setBoardId(value);
        setGroupId('');
        setGroups([]);
        setGroupsError(null);
    }, []);
    const onDataLoaded = useCallback((items) => {
        setBoards(items);
        chooseBoard(items[0]?.id ?? '');
    }, [chooseBoard]);
    useEffect(() => {
        if (!boardId) {
            setGroupsLoading(false);
            return;
        }
        let cancelled = false;
        setGroupsLoading(true);
        setGroupsError(null);
        runtime.api.getMondayGroups(boardId).then(response => {
            if (cancelled)
                return;
            const items = expectArray(response, 'monday.com groups');
            setGroups(items);
            setGroupId(items[0]?.id ?? '');
        }).catch(error => {
            if (cancelled)
                return;
            setGroups([]);
            setGroupId('');
            setGroupsError(toUserFacingMessage(error, "Couldn't load monday.com groups. Try again."));
        }).finally(() => { if (!cancelled)
            setGroupsLoading(false); });
        return () => { cancelled = true; };
    }, [boardId, groupsRetry, runtime]);
    const reset = useCallback(() => { setBoards([]); chooseBoard(''); }, [chooseBoard]);
    return { boards, boardId, groups, groupId, setGroupId, chooseBoard,
        groupsLoading, groupsError, onDataLoaded, reset,
        retryGroups: () => setGroupsRetry(n => n + 1) };
}
//# sourceMappingURL=useMondayExportState.js.map